import { Box, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button, Table, UtilButton } from '#shared/components/UI';
import { getAccountHolderLabel, getFormattedAccountNumber, getMaskedAccountLabel } from '../accountLabels';
import { getAccountRowToken } from '../buildAccountRows';
import useAccountTypeLabels from '../useAccountTypeLabels';
import { AccountRow } from '../types';
import AccountLogo from './AccountLogo';
import styles from './PaymentMethodsTable.module.scss';

const cn = withStyles(styles);

export type PaymentMethodsTableProps = {
  /** Exactly the rows to render, in the order they should appear — the caller sorts and pages. */
  accounts: AccountRow[];
  /** Whether this row is the current default for the listing being shown. */
  isDefaultRow: (account: AccountRow) => boolean;
  /** Billing lets credit cards be set as default; Operational doesn't. */
  canSetDefaultCreditCards: boolean;
  isSetDefaultDisabled: boolean;
  setDefaultLabel: string;
  onSetDefault: (account: AccountRow) => void;
  onOpenDetails: (account: AccountRow) => void;
};

// A bank row shows its routing-prefixed number, a card row its masked number. Both legs go through
// accountLabels rather than a private chain here — the fallback order belongs in one place.
function getAccountLabel(account: AccountRow): string {
  return account.Type === 'Bank Account' ? getFormattedAccountNumber(account) : getMaskedAccountLabel(account);
}

export default function PaymentMethodsTable({
  accounts,
  isDefaultRow,
  canSetDefaultCreditCards,
  isSetDefaultDisabled,
  setDefaultLabel,
  onSetDefault,
  onOpenDetails,
}: PaymentMethodsTableProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.PaymentMethodsTable' });
  const { getAccountTypeLabel } = useAccountTypeLabels();

  function canSetDefaultOn(account: AccountRow) {
    if (isDefaultRow(account)) return false;

    if (account.Type === 'Credit Card') return canSetDefaultCreditCards;

    // A bank account that is the account-wide default but is neither purpose's default has nothing
    // to switch, so the button is hidden rather than shown as a no-op.
    return !account.IsDefaultAccount || Boolean(account.IsBillingDefault) || Boolean(account.IsOperationalDefault);
  }

  return (
    <div className={cn('bankAccountTable')}>
      <Table
        id="payment-method-list"
        zebra
        columns={[
          {
            id: 'account',
            header: t('account', { defaultValue: 'Account' }),
            align: 'left',
            template: ({ data }) => (
              <Box flx="row" ai="center">
                <AccountLogo account={data} />

                <UtilButton size="medium" onClick={() => onOpenDetails(data)}>
                  {getAccountLabel(data)}
                </UtilButton>
              </Box>
            ),
          },
          {
            id: 'clientName',
            header: t('accountHolder', { defaultValue: 'Account Holder' }),
            align: 'left',
            template: ({ data }) => getAccountHolderLabel(data),
          },
          {
            id: 'type',
            header: t('accountType', { defaultValue: 'Account Type' }),
            align: 'left',
            template: ({ data }) => getAccountTypeLabel(data.Type),
          },
          {
            id: 'currency',
            header: t('currency', { defaultValue: 'Currency' }),
            align: 'left',
            field: 'Currency',
          },
          {
            id: 'view',
            header: '',
            align: 'right',
            template: ({ data }) => (
              <Box gap="sm" jc="fe">
                {canSetDefaultOn(data) && (
                  <Button
                    id={`account-detail-${getAccountRowToken(data)}`}
                    size="small"
                    disabled={isSetDefaultDisabled}
                    onClick={() => onSetDefault(data)}
                  >
                    {setDefaultLabel}
                  </Button>
                )}
              </Box>
            ),
          },
        ]}
        rows={accounts.map((account) => ({ id: getAccountRowToken(account), data: account }))}
      />
    </div>
  );
}
